import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, ShieldCheck, UserPlus, Clock, Save } from "lucide-react";
import MatchResultAlert from "../components/MatchResultAlert";
import { celebrateExactWin } from "../lib/celebration";

function Section({ title, children, testid }) {
  return (
    <div className="border border-white/10 bg-[#141414] p-6" data-testid={testid}>
      <h2 className="font-display text-2xl font-[900] uppercase mb-5 tracking-tight">{title}</h2>
      {children}
    </div>
  );
}

export default function Admin() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "admin";
  const [matches, setMatches] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [subadmins, setSubadmins] = useState([]);

  const load = async () => {
    const tasks = [api.get("/matches"), api.get("/prizes")];
    if (isSuperAdmin) tasks.push(api.get("/admin/subadmins"));
    const [m, p, s] = await Promise.all(tasks);
    setMatches(m.data);
    setPrizes(p.data);
    if (isSuperAdmin && s) setSubadmins(s.data);
  };

  useEffect(() => {
    load();
  }, []);

  // --- Add match form ---
  const [mForm, setMForm] = useState({
    home_team: "",
    home_flag: "",
    away_team: "",
    away_flag: "",
    kickoff: "",
    stage: "Group Stage",
    venue: "",
  });
  const submitMatch = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...mForm, kickoff: new Date(mForm.kickoff).toISOString() };
      await api.post("/matches", payload);
      toast.success(t("match_added"));
      setMForm({ home_team: "", home_flag: "", away_team: "", away_flag: "", kickoff: "", stage: "Group Stage", venue: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // --- Set result ---
  const [resultDraft, setResultDraft] = useState({});
  const [resultAlert, setResultAlert] = useState(null);
  const [kickoffDraft, setKickoffDraft] = useState({});
  const [editingKickoff, setEditingKickoff] = useState(null);

  const isoToLocalInput = (iso) => {
    // Convert a UTC ISO string to a "YYYY-MM-DDTHH:mm" value in Colombia time
    // suitable for <input type="datetime-local">
    try {
      const d = new Date(iso);
      const colMs = d.getTime() - 5 * 60 * 60 * 1000; // shift to UTC-5 wall clock
      const c = new Date(colMs);
      const pad = (n) => String(n).padStart(2, "0");
      return `${c.getUTCFullYear()}-${pad(c.getUTCMonth() + 1)}-${pad(c.getUTCDate())}T${pad(c.getUTCHours())}:${pad(c.getUTCMinutes())}`;
    } catch {
      return "";
    }
  };

  const localInputToIso = (val) => {
    // Treat the input as Colombia local time (UTC-5) → convert to UTC ISO
    if (!val) return null;
    const [date, time] = val.split("T");
    const [y, mo, d] = date.split("-").map(Number);
    const [hh, mm] = time.split(":").map(Number);
    // Colombia is UTC-5 → equivalent UTC time = adding 5 hours
    const utc = new Date(Date.UTC(y, mo - 1, d, hh + 5, mm));
    return utc.toISOString();
  };

  const saveKickoff = async (id) => {
    const val = kickoffDraft[id];
    if (!val) {
      toast.error(t("enter_kickoff"));
      return;
    }
    try {
      const iso = localInputToIso(val);
      await api.patch(`/matches/${id}`, { kickoff: iso });
      toast.success(t("kickoff_updated"));
      setEditingKickoff(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const setResult = async (id) => {
    const d = resultDraft[id] || {};
    if (d.home_score === undefined || d.away_score === undefined) {
      toast.error(t("enter_scores_admin"));
      return;
    }
    try {
      const res = await api.post(`/matches/${id}/result`, {
        home_score: Number(d.home_score),
        away_score: Number(d.away_score),
      });
      toast.success(t("result_saved"));
      setResultDraft((x) => ({ ...x, [id]: {} }));
      setResultAlert(res.data);
      celebrateExactWin();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // --- Prize form ---
  const emptyPrize = { title: "", description: "", position: 1, image_url: "" };
  const [pForm, setPForm] = useState(emptyPrize);
  const [editingPrizeId, setEditingPrizeId] = useState(null);

  const submitPrize = async (e) => {
    e.preventDefault();
    try {
      if (editingPrizeId) {
        await api.put(`/prizes/${editingPrizeId}`, { ...pForm, position: Number(pForm.position) });
        toast.success(t("prize_updated"));
      } else {
        await api.post("/prizes", { ...pForm, position: Number(pForm.position) });
        toast.success(t("prize_added"));
      }
      setPForm(emptyPrize);
      setEditingPrizeId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startEditPrize = (p) => {
    setEditingPrizeId(p.id);
    setPForm({
      title: p.title,
      description: p.description,
      position: p.position,
      image_url: p.image_url || "",
    });
  };

  const deletePrize = async (id) => {
    if (!window.confirm(t("delete_confirm"))) return;
    try {
      await api.delete(`/prizes/${id}`);
      toast.success(t("deleted_toast"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // --- Sub-admin form ---
  const emptySub = { email: "", name: "", username: "", password: "" };
  const [subForm, setSubForm] = useState(emptySub);

  const submitSubadmin = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/subadmins", subForm);
      toast.success(t("subadmin_added"));
      setSubForm(emptySub);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    }
  };

  const deleteSubadmin = async (id, username) => {
    if (!window.confirm(`${t("delete_subadmin_confirm")} @${username}?`)) return;
    try {
      await api.delete(`/admin/subadmins/${id}`);
      toast.success(t("deleted_toast"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const inputCls =
    "w-full h-10 px-3 bg-[#0A0A0A] border border-white/20 focus:border-[#007AFF] focus:outline-none text-white text-sm";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6" data-testid="admin-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#FF3B30]">
          {t("admin_panel")}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-[900] uppercase mt-2 mb-8">
          Command Center
        </h1>
      </div>

      <Section title={t("add_match")} testid="admin-add-match-section">
        <form onSubmit={submitMatch} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-add-match-form">
          <input
            placeholder={t("home_team")}
            value={mForm.home_team}
            onChange={(e) => setMForm({ ...mForm, home_team: e.target.value })}
            required
            className={inputCls}
            data-testid="m-home-team"
          />
          <input
            placeholder={t("home_flag")}
            value={mForm.home_flag}
            onChange={(e) => setMForm({ ...mForm, home_flag: e.target.value })}
            className={inputCls}
            data-testid="m-home-flag"
          />
          <input
            placeholder={t("away_team")}
            value={mForm.away_team}
            onChange={(e) => setMForm({ ...mForm, away_team: e.target.value })}
            required
            className={inputCls}
            data-testid="m-away-team"
          />
          <input
            placeholder={t("away_flag")}
            value={mForm.away_flag}
            onChange={(e) => setMForm({ ...mForm, away_flag: e.target.value })}
            className={inputCls}
            data-testid="m-away-flag"
          />
          <input
            type="datetime-local"
            value={mForm.kickoff}
            onChange={(e) => setMForm({ ...mForm, kickoff: e.target.value })}
            required
            className={inputCls}
            data-testid="m-kickoff"
          />
          <input
            placeholder={t("stage")}
            value={mForm.stage}
            onChange={(e) => setMForm({ ...mForm, stage: e.target.value })}
            className={inputCls}
            data-testid="m-stage"
          />
          <input
            placeholder={t("venue")}
            value={mForm.venue}
            onChange={(e) => setMForm({ ...mForm, venue: e.target.value })}
            className={`${inputCls} md:col-span-2`}
            data-testid="m-venue"
          />
          <button
            type="submit"
            className="h-10 bg-[#007AFF] hover:bg-[#3395FF] text-white text-xs font-[900] uppercase tracking-wider md:col-span-4 flex items-center justify-center gap-2"
            data-testid="m-submit"
          >
            <Plus className="w-4 h-4" />
            {t("add_match")}
          </button>
        </form>
      </Section>

      <Section title={t("set_result")} testid="admin-results-section">
        <div className="space-y-2">
          {matches.map((m) => (
            <div
              key={m.id}
              className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center border-b border-white/5 py-3"
              data-testid={`admin-match-${m.id}`}
            >
              <div className="text-sm flex items-center gap-3 flex-wrap">
                <span className="text-white/50 text-[10px] uppercase tracking-wider">{m.stage}</span>
                <span className="font-bold">
                  {m.home_flag} {m.home_team} vs {m.away_team} {m.away_flag}
                </span>
                {m.status === "finished" && (
                  <span className="text-[#34C759] text-xs font-bold">
                    {m.home_score}–{m.away_score} ✓
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  className="w-14 h-9 px-2 text-center bg-[#0A0A0A] border border-white/20 text-sm"
                  placeholder="-"
                  value={resultDraft[m.id]?.home_score ?? m.home_score ?? ""}
                  onChange={(e) =>
                    setResultDraft((x) => ({
                      ...x,
                      [m.id]: { ...x[m.id], home_score: e.target.value },
                    }))
                  }
                  data-testid={`r-home-${m.id}`}
                />
                <span className="text-white/30">:</span>
                <input
                  type="number"
                  min={0}
                  className="w-14 h-9 px-2 text-center bg-[#0A0A0A] border border-white/20 text-sm"
                  placeholder="-"
                  value={resultDraft[m.id]?.away_score ?? m.away_score ?? ""}
                  onChange={(e) =>
                    setResultDraft((x) => ({
                      ...x,
                      [m.id]: { ...x[m.id], away_score: e.target.value },
                    }))
                  }
                  data-testid={`r-away-${m.id}`}
                />
                <button
                  onClick={() => setResult(m.id)}
                  className="h-9 px-3 bg-white/10 hover:bg-white/20 text-xs font-bold uppercase tracking-wider"
                  data-testid={`r-save-${m.id}`}
                >
                  {t("save")}
                </button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title={editingPrizeId ? t("edit") + " · " + t("nav_prizes") : t("add_prize")} testid="admin-prizes-section">
        <form onSubmit={submitPrize} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="admin-prize-form">
          <input
            placeholder={t("title")}
            value={pForm.title}
            onChange={(e) => setPForm({ ...pForm, title: e.target.value })}
            required
            className={inputCls}
            data-testid="p-title"
          />
          <input
            type="number"
            min={1}
            placeholder={t("position")}
            value={pForm.position}
            onChange={(e) => setPForm({ ...pForm, position: e.target.value })}
            required
            className={inputCls}
            data-testid="p-position"
          />
          <input
            placeholder={t("image_url")}
            value={pForm.image_url}
            onChange={(e) => setPForm({ ...pForm, image_url: e.target.value })}
            className={`${inputCls} md:col-span-2`}
            data-testid="p-image"
          />
          <textarea
            placeholder={t("description")}
            value={pForm.description}
            onChange={(e) => setPForm({ ...pForm, description: e.target.value })}
            required
            rows={3}
            className={`${inputCls} h-auto md:col-span-2 py-2`}
            data-testid="p-description"
          />
          <div className="md:col-span-2 flex items-center gap-2">
            <button
              type="submit"
              className="h-10 px-6 bg-[#007AFF] hover:bg-[#3395FF] text-white text-xs font-[900] uppercase tracking-wider flex items-center gap-2"
              data-testid="p-submit"
            >
              <Plus className="w-4 h-4" />
              {editingPrizeId ? t("save") : t("add_prize")}
            </button>
            {editingPrizeId && (
              <button
                type="button"
                onClick={() => {
                  setEditingPrizeId(null);
                  setPForm(emptyPrize);
                }}
                className="h-10 px-4 bg-white/5 border border-white/15 text-xs uppercase tracking-wider font-bold"
              >
                {t("cancel")}
              </button>
            )}
          </div>
        </form>

        <div className="space-y-2">
          {prizes.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 border border-white/5 bg-[#0d0d0d] px-4 py-3"
              data-testid={`admin-prize-${p.id}`}
            >
              <span className="font-display text-xl font-[900] w-10">#{p.position}</span>
              <div className="flex-1">
                <div className="font-bold text-sm">{p.title}</div>
                <div className="text-xs text-white/50 line-clamp-1">{p.description}</div>
              </div>
              <button
                onClick={() => startEditPrize(p)}
                className="h-8 w-8 flex items-center justify-center hover:bg-white/10 border border-white/10"
                data-testid={`p-edit-${p.id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => deletePrize(p.id)}
                className="h-8 w-8 flex items-center justify-center hover:bg-[#FF3B30]/20 border border-white/10 text-[#FF3B30]"
                data-testid={`p-delete-${p.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </Section>

      {isSuperAdmin && (
        <Section
          title={
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#00D37F]" /> {t("edit_kickoff_title")}
            </span>
          }
          testid="admin-edit-kickoff-section"
        >
          <p className="text-xs text-white/50 font-mono mb-5 leading-relaxed">
            {t("edit_kickoff_description")}
          </p>
          <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
            {matches
              .filter((m) => m.status !== "finished")
              .map((m) => (
                <div
                  key={m.id}
                  className="border border-white/5 bg-[#0d0d0d] px-4 py-3"
                  data-testid={`admin-kickoff-${m.id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-[10px] font-mono uppercase tracking-wider text-[#00D37F] bg-[#00D37F]/10 border border-[#00D37F]/30 px-2 py-0.5">
                        {m.stage}
                      </span>
                      <span className="font-head text-sm truncate">
                        {m.home_flag} {m.home_team} vs {m.away_team} {m.away_flag}
                      </span>
                    </div>
                    <div className="font-mono text-[11px] text-white/60">
                      {new Date(m.kickoff).toLocaleString("es-CO", {
                        weekday: "short",
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                        timeZone: "America/Bogota",
                      })}{" "}
                      <span className="text-[#00D37F]/80">COL</span>
                    </div>
                    {editingKickoff === m.id ? (
                      <button
                        onClick={() => setEditingKickoff(null)}
                        className="h-8 px-3 bg-white/5 border border-white/15 text-[10px] uppercase tracking-wider font-head text-white/70"
                        data-testid={`k-cancel-${m.id}`}
                      >
                        {t("cancel")}
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingKickoff(m.id);
                          setKickoffDraft((x) => ({ ...x, [m.id]: isoToLocalInput(m.kickoff) }));
                        }}
                        className="h-8 px-3 inline-flex items-center gap-1.5 bg-white/5 hover:bg-[#00D37F]/10 border border-white/15 hover:border-[#00D37F]/40 text-[10px] uppercase tracking-wider font-head text-white/80"
                        data-testid={`k-edit-${m.id}`}
                      >
                        <Pencil className="w-3 h-3" /> {t("edit")}
                      </button>
                    )}
                  </div>

                  {editingKickoff === m.id && (
                    <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-white/5 pt-3">
                      <label className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/50 font-bold">
                        {t("new_kickoff")} ({t("colombia_time_short")})
                      </label>
                      <input
                        type="datetime-local"
                        value={kickoffDraft[m.id] || ""}
                        onChange={(e) =>
                          setKickoffDraft((x) => ({ ...x, [m.id]: e.target.value }))
                        }
                        className={inputCls + " max-w-[260px]"}
                        data-testid={`k-input-${m.id}`}
                      />
                      <button
                        onClick={() => saveKickoff(m.id)}
                        className="h-10 px-4 inline-flex items-center gap-2 bg-[#00D37F] hover:bg-[#00E78B] text-black text-xs font-head"
                        data-testid={`k-save-${m.id}`}
                      >
                        <Save className="w-4 h-4" /> {t("save")}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            {matches.filter((m) => m.status !== "finished").length === 0 && (
              <div className="text-xs text-white/40 font-mono uppercase tracking-wider">
                {t("no_upcoming_matches")}
              </div>
            )}
          </div>
        </Section>
      )}

      {isSuperAdmin && (
        <Section
          title={
            <span className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#00D37F]" /> {t("subadmins_title")}
            </span>
          }
          testid="admin-subadmins-section"
        >
          <p className="text-xs text-white/50 font-mono mb-5 leading-relaxed">
            {t("subadmins_description")}
          </p>

          <form
            onSubmit={submitSubadmin}
            className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6"
            data-testid="admin-subadmin-form"
          >
            <input
              placeholder={t("name")}
              value={subForm.name}
              onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
              required
              className={inputCls}
              data-testid="sub-name"
            />
            <input
              placeholder={t("username")}
              value={subForm.username}
              onChange={(e) => setSubForm({ ...subForm, username: e.target.value })}
              required
              className={inputCls}
              data-testid="sub-username"
            />
            <input
              type="email"
              placeholder={t("email")}
              value={subForm.email}
              onChange={(e) => setSubForm({ ...subForm, email: e.target.value })}
              required
              className={inputCls}
              data-testid="sub-email"
            />
            <input
              type="password"
              placeholder={t("password")}
              value={subForm.password}
              onChange={(e) => setSubForm({ ...subForm, password: e.target.value })}
              required
              minLength={6}
              className={inputCls}
              data-testid="sub-password"
            />
            <div className="md:col-span-2">
              <button
                type="submit"
                className="h-10 px-6 bg-[#00D37F] hover:bg-[#00E78B] text-black text-xs font-head tracking-wider flex items-center gap-2"
                data-testid="sub-submit"
              >
                <UserPlus className="w-4 h-4" />
                {t("create_subadmin")}
              </button>
            </div>
          </form>

          <div className="space-y-2">
            {subadmins.length === 0 ? (
              <div className="text-xs text-white/40 font-mono uppercase tracking-wider">
                {t("no_subadmins")}
              </div>
            ) : (
              subadmins.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-3 border border-white/5 bg-[#0d0d0d] px-4 py-3"
                  data-testid={`admin-subadmin-${s.id}`}
                >
                  <div className="w-9 h-9 bg-[#00D37F]/15 border border-[#00D37F]/30 flex items-center justify-center text-xs font-head text-[#00D37F]">
                    {s.username?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">
                      @{s.username}{" "}
                      <span className="text-white/40 text-xs font-normal">· {s.name}</span>
                    </div>
                    <div className="text-xs text-white/50 truncate font-mono">{s.email}</div>
                  </div>
                  <span className="hidden sm:inline-block text-[9px] uppercase tracking-[0.25em] font-mono font-bold text-[#00D37F] bg-[#00D37F]/10 border border-[#00D37F]/30 px-2 py-0.5">
                    SUB-ADMIN
                  </span>
                  <button
                    onClick={() => deleteSubadmin(s.id, s.username)}
                    className="h-8 w-8 flex items-center justify-center hover:bg-[#FF2D45]/20 border border-white/10 text-[#FF2D45]"
                    data-testid={`sub-delete-${s.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </Section>
      )}
      {resultAlert && (
        <MatchResultAlert result={resultAlert} onClose={() => setResultAlert(null)} />
      )}
    </div>
  );
}
